import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";

import useTranslations from "@/shared/lang/useTranslations";

import { AuthShell } from "../components/AuthShell";
import { RegisterForm } from "../components/RegisterForm";
import { useRegister } from "../hooks/useRegister";
import { useTurnstile } from "../hooks/useTurnstile";
import type { PreferredLanguage } from "../types/register.types";

export function RegisterPage() {
  const { activeLanguage } = useTranslations("register");
  const { t: tl } = useTranslations("login");
  const navigate = useNavigate();
  const { isLoading, error, emailConflict, success, doRegister, clearError, reset } = useRegister();
  const turnstile = useTurnstile("register");

  // On register success the user is authenticated but `email_verified_at` is null.
  // Send them to the bloqueante verify screen; the gate in PrivateRoute would
  // re-route them anyway, but jumping straight there avoids a `/app` flash.
  useEffect(() => {
    if (success) {
      reset();
      navigate("/auth/register/verify", { replace: true });
    }
  }, [success, navigate, reset]);

  return (
    <AuthShell panelSide="left">
      <div className="flex w-full flex-col items-center gap-6">
        {/* The heading lives inside RegisterForm: its subtitle depends on the account type, which is RHF
            state owned by the form. It renders as a fragment sibling of the <form>, so it stays a direct
            child of this flex container and keeps the gap-6. */}
        <RegisterForm
          key={activeLanguage}
          isLoading={!!isLoading}
          error={error}
          emailConflict={emailConflict}
          preferredLanguage={activeLanguage as PreferredLanguage}
          onSubmit={(body) => {
            doRegister(body, turnstile.token);
            turnstile.reset();
          }}
          onClearError={clearError}
          turnstile={turnstile}
        />

        <p className="text-muted-foreground text-sm">
          {tl("login_account_label_prefix")}{" "}
          <Link to="/auth/login" className="text-foreground underline">
            {tl("login_account_label_button")}
          </Link>
        </p>
      </div>
    </AuthShell>
  );
}
